import type { ReactNode } from 'react';

import { useCallback, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { useFormContext } from 'react-hook-form';

import Button from '@/components/Button';
import PredictiveSelect from '@/components/PredictiveSelect';

import { useDashboard } from '@/hooks/useDashboard';
import { getAgents, getUnderwriters } from '@/services/webservices/cpp';

type FilterCategoryProps = {
	children?: ReactNode;
	filterKey: string;
	title: string;
};

type FilterCheckboxProps = {
	filterName: string;
	label: string;
	value: string;
};

type FilterDrawerProps = {
	onAccept: any;
};

const coverageTypes = [
	{ value: 'type1', label: 'Type 1' },
	{ value: 'type2', label: 'Type 2' },
	{ value: 'type3', label: 'Type 3' }
];

const programCodes = [
	{ value: 'code1', label: 'Code 1' },
	{ value: 'code2', label: 'Code 2' },
	{ value: 'code3', label: 'Code 3' }
];

function FilterCheckbox({
	filterName,
	value,
	label
}: FilterCheckboxProps ) {
	const { register } = useFormContext();

	return (
		<div className="mb-2">
			<label className="inline-flex gap-x-2">
				<input
					type="checkbox"
					value={ value }
					{ ...register( filterName, { required: false } ) }
				/>
				<span className="text-md font-thin">{ label }</span>
			</label>
		</div>
	);
}

function FilterCategory({
	children,
	filterKey,
	title
}: FilterCategoryProps ) {
	const { filters } = useDashboard();
	const [ isOpen, setIsOpen ] = useState( false );

	const toggleOpen = useCallback( () => {
		setIsOpen( !isOpen );
	}, [ isOpen, setIsOpen ] );

	const filterValueString = useMemo( () => {
		const filterValue = ( filters as any )[ filterKey ];

		let returnString = ''
		
		if ( Array.isArray( filterValue ) ) {
			returnString = filterValue.map( filter => {
				return filter.label || filter.value || filter;
			}).join( ', ' );
		}

		return returnString;
	}, [ filterKey, filters ] );

	return (
		<div className="border-b border-rural-gray-500">
			<button className="flex justify-between items-center py-4 w-full" type="button" onClick={ () => toggleOpen() }>
				<h3 className={ `text-xl ${ filterValueString ? 'font-bold' : 'font-thin' }` }>{ title }</h3>

				<Image
					src="/images/arrow-right.svg"
					width={ 24 }
					height={ 24 }
					alt={ isOpen ? 'Close' : 'Open' }
					className={ isOpen ? 'rotate-90' : '' }
				/>
			</button>

			{ filterValueString && !isOpen && (
				<div className="mb-8">
					{ filterValueString }
				</div>
			)}

			{ isOpen && (
				<div className="mb-8">
					{ children }
				</div>
			)}
		</div>
	);
}

export default function FilterDrawer({
	onAccept = () => {}
}: FilterDrawerProps ) {
	const { filters, setFilters } = useDashboard();
	const { handleSubmit, reset } = useFormContext();

	const onSubmit = ( data: any ) => {
		setFilters( data );

		onAccept();
	};

	const onClear = () => {
		setFilters( {} );

		reset();
		
		onAccept();
	};

	useEffect( () => {
		reset( filters );
	}, [ filters, reset ] );

	return (
		<form className="flex flex-1" onSubmit={ handleSubmit( onSubmit ) }>
			<div className="mt-4 flex flex-1 flex-col border-t border-t-rural-gray-500">
				<FilterCategory title="Agent" filterKey="agent">
					<PredictiveSelect
						name="agent"
						defaultValue={ filters.agent }
						noOptionsMessage="No agents found"
						placeholder="Search by agent name..."
						queryKey="agents"
						queryFunction={ getAgents }
					/>
				</FilterCategory>
				
				{/* <FilterCategory title="Coverage" filterKey="coverage">
					{ coverageTypes.map( type => (
						<FilterCheckbox
							key={ type.value }
							filterName="coverage"
							label={ type.label }
							value={ type.value }
						/>
					))}
				</FilterCategory>

				<FilterCategory title="Program code" filterKey="programCode">
					{ programCodes.map( code => (
						<FilterCheckbox
							key={ code.value }
							filterName="programCode"
							label={ code.label }
							value={ code.value }
						/>
					))}
				</FilterCategory> */}
				
				<FilterCategory title="Underwriter" filterKey="underwriter">
					<PredictiveSelect
						defaultValue={ filters.underwriter }
						name="underwriter"
						noOptionsMessage="No underwriters found"
						placeholder="Search by underwriter name..."
						queryFunction={ getUnderwriters }
						queryKey="underwriters"
					/>
				</FilterCategory>

				<div className="mt-auto mb-8 flex gap-x-4">
					<Button type="submit" size="large" variant="primary">Apply filters</Button>
					<Button type="button" size="large" variant="border" onClick={ onClear }>Reset</Button>
				</div>
			</div>
		</form>
	);
}
